import { TelegramThemeParams } from '../types/telegram';

/**
 * Parses a `#rgb` or `#rrggbb` hex colour (leading `#` optional) into a
 * space-separated RGB channel triplet, e.g. '#3538CD' -> '53 56 205'.
 * This is the format `tokens.css` and the Tailwind config expect for the
 * `--c-*` role tokens (so `rgb(var(--c-brand) / <alpha-value>)` works).
 * Returns null for anything that isn't a valid 3- or 6-digit hex colour.
 */
export function hexToChannels(hex: string): string | null {
  if (typeof hex !== 'string') return null;
  const cleaned = hex.trim().replace(/^#/, '');

  let r: number;
  let g: number;
  let b: number;

  if (/^[0-9a-fA-F]{3}$/.test(cleaned)) {
    r = parseInt(cleaned[0] + cleaned[0], 16);
    g = parseInt(cleaned[1] + cleaned[1], 16);
    b = parseInt(cleaned[2] + cleaned[2], 16);
  } else if (/^[0-9a-fA-F]{6}$/.test(cleaned)) {
    r = parseInt(cleaned.slice(0, 2), 16);
    g = parseInt(cleaned.slice(2, 4), 16);
    b = parseInt(cleaned.slice(4, 6), 16);
  } else {
    return null;
  }

  return `${r} ${g} ${b}`;
}

/** Mixes `mixHex` into `baseHex` at `ratio` (0..1), channel by channel. Null if either colour is invalid. */
function mixChannels(baseHex: string, mixHex: string, ratio: number): string | null {
  const base = hexToChannels(baseHex);
  const mix = hexToChannels(mixHex);
  if (!base || !mix) return null;

  const baseParts = base.split(' ').map(Number);
  const mixParts = mix.split(' ').map(Number);
  const blended = baseParts.map((channel, i) => Math.round(channel * (1 - ratio) + mixParts[i] * ratio));
  return blended.join(' ');
}

/**
 * Maps Telegram's `themeParams` onto the app's role tokens (the `--c-*` RGB
 * channel variables from `src/styles/tokens.css`), so every primitive built
 * on those roles automatically follows Telegram's colours. Also marks the
 * root with `data-surface="telegram"` (spec 4.9) and keeps writing the
 * legacy `--tg-theme-*` vars in case other CSS still reads them.
 *
 * `--c-signal`/`--c-on-signal` are intentionally left untouched — Mango
 * stays the same "new/now" signal colour in every surface (spec 4.2/4.9).
 */
export function applyTelegramTokens(params: TelegramThemeParams, root: HTMLElement = document.documentElement): void {
  root.setAttribute('data-surface', 'telegram');

  const setChannels = (prop: string, hex?: string) => {
    const channels = hex ? hexToChannels(hex) : null;
    if (channels) root.style.setProperty(prop, channels);
  };

  const bg = params.bg_color;
  const secondaryBg = params.secondary_bg_color || params.bg_color;

  setChannels('--c-surface-page', bg);
  setChannels('--c-surface-card', secondaryBg);
  setChannels('--c-surface-sunken', secondaryBg);
  setChannels('--c-text', params.text_color);
  setChannels('--c-text-muted', params.hint_color);
  setChannels('--c-brand', params.button_color);
  setChannels('--c-brand-hover', params.button_color);
  setChannels('--c-on-brand', params.button_text_color);
  setChannels('--c-brand-soft', secondaryBg);
  setChannels('--c-on-brand-soft', params.link_color);
  setChannels('--c-line-strong', params.hint_color);

  if (bg && params.hint_color) {
    const line = mixChannels(bg, params.hint_color, 0.3);
    if (line) root.style.setProperty('--c-line', line);
  }

  // Legacy vars — kept for any CSS that still reads --tg-theme-* directly.
  const setLegacy = (prop: string, hex?: string) => {
    if (hex) root.style.setProperty(prop, hex);
  };
  setLegacy('--tg-theme-bg-color', params.bg_color);
  setLegacy('--tg-theme-text-color', params.text_color);
  setLegacy('--tg-theme-hint-color', params.hint_color);
  setLegacy('--tg-theme-link-color', params.link_color);
  setLegacy('--tg-theme-button-color', params.button_color);
  setLegacy('--tg-theme-button-text-color', params.button_text_color);
  setLegacy('--tg-theme-secondary-bg-color', params.secondary_bg_color);
}
